<template>
    <div>
        <v-dialog
            v-model="dialogProxy"
            max-width="1100px"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            :aria-describedby="descId"
            :persistent="false"
            eager
            :retain-focus="true"
            @click:outside="$emit('close')"
            @keydown.esc.native="$emit('close')">
            <v-card v-focus-trap="dialogProxy" id="workflow">
                <!-- your existing card content remains unchanged down to the closing tags -->
                <v-card-title :id="titleId" style="font-size: 18px" class="ml-1"
                    >Content Workflow
                    <v-btn
                        data-focus-first
                        ref="closeBtn"
                        class="qchip"
                        @click="$emit('close')"
                        style="background-color: rgb(246, 246, 246); top: 14px"
                        x-small
                        fab
                        outlined
                        color="primary"
                        absolute
                        right
                        :aria-label="'Close dialog'"
                        :autofocus="true">
                        <v-icon aria-hidden="true">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <p class="sr-only" :id="descId">This dialog shows the content workflow. Use the stepper to navigate stages. Press Escape to close.</p>

                <v-card-subtitle
                    ><v-chip outlined color="primary" small class="mt-1 mb-2 qchip">{{ project ? project.title : "" }}</v-chip></v-card-subtitle
                >

                <v-stepper v-model="currentStep" style="box-shadow: none">
                    <v-divider style="border-color: rgb(237, 239, 241); border-width: 2px"></v-divider>
                    <!-- ---------------------------- -->
                    <!-- ------  Steps Header  ------ -->
                    <!-- ---------------------------- -->
                    <v-stepper-header
                        class="workflowHeader"
                        role="tablist"
                        :aria-label="'Workflow steps'"
                        @keydown.left.prevent="goPrevStep"
                        @keydown.right.prevent="goNextStep"
                        @keydown.home.prevent="goFirstStep"
                        @keydown.end.prevent="goLastStep">
                        <template v-for="(stage, index) in workflow.stages">
                            <v-divider v-if="index > 0" :class="isStageComplete(stage) ? 'active mx-3' : 'inactive mx-3'" style="border-color: rgb(237, 239, 241); border-width: 1px" />
                            <v-stepper-step
                                color="#4caf50"
                                editable
                                edit-icon="mdi-check"
                                :complete="isStageComplete(stage)"
                                :step="index + 1"
                                :key="index"
                                role="tab"
                                :id="`step-tab-${index + 1}`"
                                :aria-selected="currentStep === index + 1 ? 'true' : 'false'"
                                :aria-controls="`step-panel-${index + 1}`"
                                :aria-current="currentStep === index + 1 ? 'step' : undefined">
                                {{ stage.title }}
                            </v-stepper-step>
                        </template>
                    </v-stepper-header>

                    <v-divider style="border-color: rgb(237, 239, 241); border-width: 2px"></v-divider>

                    <!-- ---------------------------- -->
                    <!-- ------  Steps Content  ------ -->
                    <!-- ---------------------------- -->
                    <v-stepper-content
                        class="px-0"
                        v-for="(stage, index) in workflow.stages"
                        :key="index"
                        :step="index + 1"
                        :id="`step-panel-${index + 1}`"
                        role="tabpanel"
                        :aria-labelledby="`step-tab-${index + 1}`">
                        <v-card class="mb-12" flat>
                            <!-- Button to add new tasks -->
                            <v-btn class="qchip" :disabled="$store.state.content_suite_group.id !== 1" right absolute rounded small outlined @click="openNewTaskDialog(index)" color="primary">
                                <v-icon small>mdi-plus</v-icon> Task
                            </v-btn>
                            <v-data-table id="workflowTaskTable" hide-default-footer :headers="taskHeaders" :items="stage.tasks" :item-key="'_id'" class="elevation-0">
                                <template v-slot:item="{ item, index }">
                                    <tr :class="getRowClass(item)">
                                        <!-- --------------------------- -->
                                        <!-- ------     Check     ------ -->
                                        <!-- --------------------------- -->
                                        <td style="border-left: 3px solid rgb(220, 220, 220)">
                                            <div class="d-flex">
                                                <v-checkbox
                                                    :disabled="$store.state.content_suite_group.id !== 1 && $store.state.content_suite_group.id !== 2"
                                                    class="py-auto"
                                                    on-icon="mdi-check-circle-outline"
                                                    off-icon="mdi-checkbox-blank-circle-outline"
                                                    v-model="item.completed"
                                                    color="success"
                                                    @click.stop="toggleTask(item)"
                                                    @keydown.enter.native.stop.prevent="toggleTask(item)"></v-checkbox>

                                                <v-icon class="pb-1" v-if="item.dueDate && isOverdue(item.dueDate) && !item.completed" absolute right top color="warning">
                                                    mdi-timer-alert-outline
                                                </v-icon>
                                            </div>
                                        </td>
                                        <!-- --------------------------- -->
                                        <!-- ------     Title     ------ -->
                                        <!-- --------------------------- -->

                                        <td class="py-3">
                                            {{ item.title }}
                                        </td>

                                        <!-- --------------------------- -->
                                        <!-- ------   Due Date    ------ -->
                                        <!-- --------------------------- -->
                                        <td>
                                            <v-chip
                                                class="pl-1"
                                                outlined
                                                role="button"
                                                tabindex="0"
                                                :disabled="$store.state.content_suite_group.id !== 1 && $store.state.content_suite_group.id !== 2"
                                                :aria-haspopup="'dialog'"
                                                :aria-expanded="dueDateFor === item ? 'true' : 'false'"
                                                :aria-label="item.dueDate ? 'Change due date, current ' + formatDateShort(item.dueDate) : 'Set due date'"
                                                :ref="`dueChip-${item._id || index}`"
                                                :data-activator-id="`dueChip-${item._id || index}`"
                                                @click.stop="openDueDateDialog(item, index, $event)"
                                                @keydown.enter.stop.prevent="openDueDateDialog(item, index, $event)"
                                                @keydown.space.stop.prevent="openDueDateDialog(item, index, $event)">
                                                <span v-if="item.dueDate">
                                                    <v-avatar color="primary" style="font-size: 10px" class="avat mr-2">
                                                        <v-icon color="white" size="16">mdi-calendar</v-icon>
                                                    </v-avatar>
                                                    {{ formatDateShort(item.dueDate) }}
                                                </span>
                                                <span v-else>
                                                    <v-avatar color="primary" style="font-size: 10px" class="avat mr-2 white--text">
                                                        <v-icon color="white" size="16" small>mdi-calendar</v-icon>
                                                    </v-avatar>
                                                    <v-icon small color="grey">mdi-plus</v-icon>
                                                </span>
                                            </v-chip>

                                            <!-- Dialog with date picker -->
                                            <v-dialog
                                                v-model="item.__dueDialogOpen"
                                                max-width="328"
                                                role="dialog"
                                                aria-modal="true"
                                                eager
                                                :retain-focus="true"
                                                :aria-label="'Pick due date'"
                                                @input="onDueDialogToggle(item, index)"
                                                @keydown.esc.native.prevent="onEscDueDate(item)">
                                                <v-focus-trap
                                                    :active="!!item.__dueDialogOpen"
                                                    :escape-deactivates="false"
                                                    :return-focus-on-deactivate="false"
                                                    :initial-focus="() => getFirstPickerButtonRef(item, index)"
                                                    :fallback-focus="() => getPickerRootEl(item, index) || $el">
                                                    <v-card>
                                                        <v-card-title>
                                                            Select a due date
                                                            <v-spacer />
                                                            <v-btn icon :aria-label="'Close date picker dialog'" @click="closeDueDateDialog(item)">
                                                                <v-icon>mdi-close</v-icon>
                                                            </v-btn>
                                                        </v-card-title>
                                                        <v-divider />

                                                        <v-card-text>
                                                            <v-date-picker
                                                                :ref="`dp-${item._id || index}`"
                                                                :value="toPickerDate(item.dueDate)"
                                                                @input="(val) => setDueDateAndClose(item, val)"
                                                                no-title
                                                                :first-day-of-week="1" />
                                                        </v-card-text>

                                                        <v-card-actions>
                                                            <v-spacer />
                                                            <v-btn text @click="clearDueDate(item)">Clear</v-btn>
                                                            <v-btn color="primary" @click="closeDueDateDialog(item)">OK</v-btn>
                                                        </v-card-actions>
                                                    </v-card>
                                                </v-focus-trap>
                                            </v-dialog>
                                        </td>

                                        <!-- -------------------------- -->
                                        <!-- ------  Completion  ------ -->
                                        <!-- -------------------------- -->
                                        <td v-if="item.completionDate">{{ new Date(item.completionDate).toLocaleDateString() }}</td>
                                        <td v-else>-</td>
                                        <!-- --------------------------- -->
                                        <!-- ------  Responsible  ------ -->
                                        <!-- --------------------------- -->
                                        <td>
                                            <v-chip
                                                :data-activator-id="`resp-${item._id || index}`"
                                                :ref="`resp-${item._id || index}`"
                                                class="pl-1"
                                                outlined
                                                role="button"
                                                tabindex="0"
                                                @click="openResponsibleDialog(item, $event)"
                                                @keydown.enter.stop.prevent="openResponsibleDialog(item, $event)"
                                                @keydown.space.stop.prevent="openResponsibleDialog(item, $event)">
                                                <span v-if="item.responsible.groups.length > 0"> </span>
                                                <span v-else>
                                                    <span v-if="item.responsible.user[0]">
                                                        <v-avatar
                                                            :style="{ backgroundColor: $helpers.stringToBackgroundColor(item.responsible.user[0].username) }"
                                                            style="font-size: 10px"
                                                            class="mr-2 white--text"
                                                            >{{ $helpers.getInitials(item.responsible.user[0].username) }}</v-avatar
                                                        >
                                                        {{ item.responsible.user[0].username }}
                                                    </span>
                                                    <span v-else>
                                                        <v-avatar color="primary" style="font-size: 10px" class="avat mr-2 white--text"
                                                            ><v-icon size="16" small>mdi-account-multiple</v-icon>
                                                        </v-avatar>
                                                        <!-- <v-icon small>mdi-account-multiple-plus</v-icon> -->
                                                        <v-icon small color="grey">mdi-plus</v-icon>
                                                    </span>
                                                </span>
                                            </v-chip>
                                            <span style="font-size: 11px; color: rgb(88, 88, 88)" class="my-auto" v-if="item.responsible.user.length > 1">
                                                +{{ item.responsible.user.length - 1 }} more</span
                                            >
                                        </td>
                                        <td>
                                            <v-btn rounded color="primary" class="px-2" text @click="openCommentsSidebar(item, $event)" small>
                                                {{ item.comments.length }} <v-icon small>mdi-comment-text-multiple-outline</v-icon></v-btn
                                            >
                                            <v-btn :disabled="$store.state.content_suite_group.id !== 1" @click="stage.tasks.splice(index, 1)" small rounded color="red darken-4" class="px-2" text>
                                                <v-icon small>mdi-delete</v-icon></v-btn
                                            >
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-stepper-content>
                </v-stepper>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :disabled="!(currentStep > 1)" rounded @click="currentStep--" color="primary">Back</v-btn>
                    <v-btn :disabled="!(currentStep < this.workflow.stages.length)" rounded @click="currentStep++" color="primary">Next</v-btn>
                </v-card-actions>

                <!-- ------------------------------- -->
                <!-- ------  Comment Sidebar  ------ -->
                <!-- ------------------------------- -->
                <v-slide-x-transition>
                    <v-navigation-drawer width="350" v-if="commentsSidebar" v-model="commentsSidebar" right absolute temporary>
                        <div style="display: flex; flex-direction: column; height: 100%">
                            <v-card flat style="flex: 1; overflow-y: auto">
                                <v-card id="workflow">
                                    <v-card-title :id="titleId" class="ml-1" style="font-size: 18px">
                                        Content Workflow
                                        <v-btn
                                            ref="commentCloseBtn"
                                            class="qchip"
                                            @click="closeCommentsSidebarAndRestoreFocus"
                                            style="background-color: rgb(246, 246, 246); top: 14px"
                                            x-small
                                            fab
                                            outlined
                                            color="primary"
                                            absolute
                                            right
                                            :aria-label="'Close dialog'">
                                            <v-icon aria-hidden="true">mdi-close</v-icon>
                                        </v-btn>
                                    </v-card-title>

                                    <!-- SR live region: announces step changes -->
                                    <p class="sr-only" aria-live="polite" aria-atomic="true" :id="`${descId}-live`">
                                        Step {{ currentStep }} of {{ workflow.stages.length }} —
                                        {{ workflow.stages[currentStep - 1] ? workflow.stages[currentStep - 1].title : "" }}
                                    </p>

                                    <v-card-subtitle>
                                        <v-chip outlined color="primary" small class="mt-1 mb-2 qchip">
                                            {{ project ? project.title : "" }}
                                        </v-chip>
                                    </v-card-subtitle>
                                    <v-divider style="border-color: rgb(237, 239, 241); border-width: 2px"></v-divider>
                                </v-card>
                                <div class="d-flex" v-if="selectedTask.comments.length == 0">
                                    <span class="mx-auto mt-5" style="color: grey; font-size: 14px">no comments yet...</span>
                                </div>
                                <!-- Chat-Bereich mit Scrollbar -->
                                <v-list ref="commentList" dense class="comment-list" style="overflow-y: auto; flex: 1">
                                    <v-list-item v-for="(comment, index) in selectedTask.comments" :key="index">
                                        <v-list-item-avatar>
                                            <v-avatar style="font-size: 15px" :style="{ backgroundColor: $helpers.stringToBackgroundColor(comment.user.username) }" class="pr-2 white--text">{{
                                                $helpers.getInitials(comment.user.username)
                                            }}</v-avatar>
                                        </v-list-item-avatar>
                                        <div class="comment-area">
                                            <v-list-item-title>
                                                <span class="comment-user">{{ comment.user.username }} </span>
                                                <span class="comment-date">{{ $helpers.formatRelativeDate(comment.date) }}</span>
                                            </v-list-item-title>
                                            <div class="comment-text">{{ comment.content }}</div>
                                        </div>
                                    </v-list-item>
                                </v-list>
                            </v-card>

                            <!-- Bereich für v-textarea und Button -->

                            <v-card v-if="$store.state.content_suite_group.id == 1 || $store.state.content_suite_group.id == 2" flat class="quaroshadow">
                                <v-divider style="border-color: rgb(237, 239, 241); border-width: 2px"></v-divider>

                                <v-card-text style="display: flex; align-items: center">
                                    <v-textarea ref="commentTextarea" rows="1" hide-details dense rounded filled v-model="newComment" label="Add a new comment..." style="flex: 1; margin-right: 8px">
                                    </v-textarea>
                                    <v-btn ref="commentSendBtn" icon @click="addNewComment" :aria-label="'Send comment'" rounded color="primary">
                                        <v-icon>mdi-send</v-icon>
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-navigation-drawer>
                </v-slide-x-transition>
            </v-card>
        </v-dialog>

        <!-- ---------------------------- -->
        <!-- ------  ADD Task Dialog  ------ -->
        <!-- ---------------------------- -->
        <v-dialog v-model="newTaskDialog" max-width="400">
            <v-card>
                <v-card-title>
                    New Task
                    <v-btn right absolute icon @click="newTaskDialog = false" color="primary"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle v-if="selectedStageIndex != null">{{ workflow.stages[selectedStageIndex].title }}</v-card-subtitle>
                <v-card-text>
                    <v-text-field @keyup.enter="addNewTask" hide-details rounded filled v-model="newTaskTitle" label="Task Title"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-btn class="mx-auto" @click="addNewTask" rounded color="primary">create</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <responsible-selection
            v-model="responsibleDialog"
            :user="selectedTask ? selectedTask.responsible.user : []"
            @assign="assignResponsible"
            @closed="delayedRestore"
            @input="
                (val) => {
                    if (!val) delayedRestore()
                }
            " />
    </div>
</template>

<script>
    import ResponsibleSelection from "./AddResponsible.vue" // Passe den Pfad entsprechend deiner Dateistruktur an

    export default {
        components: {
            ResponsibleSelection
        },
        filters: {
            formatDate(date) {
                return new Date(date).toLocaleString() // Hier kannst du das Datumsformat anpassen
            }
        },
        props: {
            project: Object,
            show: Boolean,
            stageToShow: Number,
            shared_company_id: String
        },
        data() {
            return {
                newComment: "",
                responsibleDialog: false,
                dueDateFor: null,
                dueDateRef: null,
                selectedTask: null,
                newTaskDialog: false,
                newTaskTitle: "",
                selectedStageIndex: null,
                commentsSidebar: false,
                selectedTaskComments: [],
                currentStep: 1,
                dialog: false,
                taskHeaders: [
                    { sortable: false, text: "", value: "check" },
                    { sortable: false, text: "Task", value: "title" },
                    { sortable: false, text: "Due Date", value: "dueDate" },
                    { sortable: false, text: "Completion Date", value: "completionDate" },
                    { sortable: false, text: "Responsible", value: "responsible" },
                    { sortable: false, text: "", value: "comments", width: "150px" }
                ],
                workflow: {
                    title: "Default Content Workflow",
                    stages: [
                        {
                            title: "Briefing",
                            tasks: []
                        },
                        {
                            title: "Production",
                            tasks: []
                        },
                        {
                            title: "Lectorate",
                            tasks: []
                        },
                        {
                            title: "Publishing",
                            tasks: []
                        }
                    ]
                },
                default_workflow: {
                    title: "Default Content Workflow",
                    stages: [
                        {
                            title: "Briefing",
                            tasks: [
                                {
                                    title: "Add Briefing Description",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                },
                                {
                                    title: "Review Briefing",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                }
                            ]
                        },
                        {
                            title: "Production",
                            tasks: [
                                {
                                    title: "Create Outline",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                },
                                {
                                    title: "Write Content",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                }
                            ]
                        },
                        {
                            title: "Lectorate",
                            tasks: [
                                {
                                    title: "Edit Content",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                },
                                {
                                    title: "Review Content",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                }
                            ]
                        },
                        {
                            title: "Publishing",
                            tasks: [
                                {
                                    title: "Prepare for Publishing",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                },
                                {
                                    title: "Publish Content",
                                    responsible: { user: [], groups: [] },
                                    dueDate: null,
                                    completionDate: null,
                                    completed: false,
                                    comments: []
                                }
                            ]
                        }
                    ]
                }
            }
        },
        computed: {
            dialogProxy: {
                get() {
                    return this.show
                },
                set() {
                    this.$emit("close")
                }
            },
            userToNotify() {
                const users = []
                const groups = []
                const current_user = {
                    user: this.CryptoJS.AES.decrypt(localStorage.user, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8),
                    username: this.CryptoJS.AES.decrypt(localStorage.username, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                }
                this.workflow.stages.forEach((stage) => {
                    stage.tasks.forEach((task) => {
                        task.responsible.user.forEach((user) => {
                            if (user.user !== current_user.user) {
                                const existingUser = users.find((existing) => existing.user === user.user)
                                if (!existingUser) users.push({ user: user.user, username: user.username })
                            }
                        })
                        task.responsible.groups.forEach((group) => {
                            if (!groups.includes(group)) groups.push(group)
                        })
                        task.comments.forEach((comment) => {
                            if (comment.user.user !== current_user.user) {
                                const existingUser = users.find((existing) => existing.user === comment.user.user)
                                if (!existingUser) users.push(comment.user)
                            }
                        })
                    })
                })
                return { user: users, groups }
            },
            obi() {
                return this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8) == 1547
            },
            titleId() {
                return "workflow-dialog-title"
            },
            descId() {
                return "workflow-dialog-desc"
            }
        },
        watch: {
            commentsSidebar(val) {
                if (val) {
                    this.manageSidebarFocus()
                    document.addEventListener("keydown", this._handleEscClose)
                } else {
                    this.removeSidebarFocusTrap()
                    document.removeEventListener("keydown", this._handleEscClose)
                }
            },
            show(val) {
                if (!val) return
                this.fetchWorkflow()
            },
            workflow: {
                deep: true, // Hiermit überwachst du auch Änderungen in verschachtelten Objekten
                handler(newWorkflow, oldWorkflow) {
                    console.log("Workflow has changed:", newWorkflow)
                    this.updateCurrentStage()
                    this.saveWorkflow()
                    // Hier kannst du weitere Aktionen ausführen, wenn sich das Workflow-Objekt ändert
                }
            },
            currentStep() {
                this.$nextTick(() => this.focusCurrentStep())
            }
        },
        methods: {
            toggleTask(item) {
                if (!item.completed) {
                    item.completed = true
                    item.completionDate = new Date()
                    this.createTaskMailNotificaton(item, this.userToNotify.user, "notifiy_task_completed_mail_en.html", "A task has been completed!")
                } else {
                    item.completed = false
                    item.completionDate = null
                }

                if (this.isStageComplete(this.workflow.stages[this.currentStep - 1])) {
                    setTimeout(() => {
                        if (this.currentStep < this.workflow.stages.length) this.currentStep++
                    }, 350)
                }
            },

            closeCommentsSidebarAndRestoreFocus() {
                this.commentsSidebar = false
                this.restoreCommentActivatorFocus()
            },
            _handleEscClose(e) {
                if (e.key === "Escape") {
                    this.closeCommentsSidebarAndRestoreFocus()
                }
            },
            restoreCommentActivatorFocus() {
                this.$nextTick(() => {
                    const el = this._lastCommentActivator
                    if (el && typeof el.focus === "function") {
                        el.focus()
                    }
                    this._lastCommentActivator = null
                })
            },
            manageSidebarFocus() {
                this.$nextTick(() => {
                    const focusables = [
                        this.$refs.commentCloseBtn && this.$refs.commentCloseBtn.$el,
                        this.$refs.commentTextarea && this.$refs.commentTextarea.$el.querySelector("textarea"),
                        this.$refs.commentSendBtn && this.$refs.commentSendBtn.$el
                    ].filter(Boolean)

                    // erster Fokus aufs Textfeld
                    if (focusables[1]) focusables[1].focus()

                    // Keydown-Event für Tab-Zyklus
                    this._sidebarKeyHandler = (e) => {
                        if (e.key === "Tab") {
                            const currentIndex = focusables.indexOf(document.activeElement)
                            const nextIndex = e.shiftKey ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1) : currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1
                            focusables[nextIndex].focus()
                            e.preventDefault()
                        }
                    }

                    document.addEventListener("keydown", this._sidebarKeyHandler)
                })
            },
            removeSidebarFocusTrap() {
                document.removeEventListener("keydown", this._sidebarKeyHandler)
                this._sidebarKeyHandler = null
            },
            getPickerRootEl(item, index) {
                const comp = this.$refs[`dp-${item._id || index}`]
                return comp && comp.$el ? comp.$el : null
            },
            getFirstPickerButtonRef(item, index) {
                // Prefer the selected day, else first day button (works in Vuetify 2.3.8)
                const root = this.getPickerRootEl(item, index)
                if (!root) return null
                return root.querySelector(".v-date-picker-table .v-btn--active") || root.querySelector(".v-date-picker-table button") || root.querySelector('button,[tabindex]:not([tabindex="-1"])')
            },

            goPrevStep() {
                if (this.currentStep > 1) {
                    this.currentStep--
                }
            },
            goNextStep() {
                if (this.currentStep < this.workflow.stages.length) {
                    this.currentStep++
                }
            },
            goFirstStep() {
                this.currentStep = 1
            },
            goLastStep() {
                this.currentStep = this.workflow.stages.length
            },
            onEscDueDate(item) {
                // Close immediately…
                this.closeDueDateDialog(item)
                // …then restore focus AFTER transitions/animations have completed.
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setTimeout(() => this.restoreActivatorFocus(), 40)
                        })
                    })
                })
            },
            delayedRestore() {
                // zwei RAFs + kleiner Timeout: stabil nach Dialog-Transition
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(() => this.restoreActivatorFocus(), 40)
                    })
                })
            },
            openDueDateDialog(item, index, evt) {
                // mark the current task and remember activator
                this.selectedTask = item
                this.saveAndBlurActivator(evt)
                // open dialog flag on the item (scoped state)
                this.$set(item, "__dueDialogOpen", true)
            },
            onDueDialogToggle(item, index) {
                if (item.__dueDialogOpen) {
                    // Focus is handled by v-focus-trap :initial-focus
                    this.$nextTick(() => {}) // no-op
                } else {
                    // Close → return focus to the opening chip
                    this.$nextTick(() => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                setTimeout(() => this.restoreActivatorFocus(), 40)
                            })
                        })
                    })
                }
            },

            setDueDateAndClose(item, val) {
                // 1) set the value
                item.dueDate = val // 'YYYY-MM-DD'

                // 2) close the dialog
                this.closeDueDateDialog(item)

                // 3) restore focus AFTER the dialog actually closed (post-transition)
                //    two RAFs + short timeout = robust across browsers/animations
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setTimeout(() => this.restoreActivatorFocus(), 40)
                        })
                    })
                })
            },

            closeDueDateDialog(item) {
                if (item && item.__dueDialogOpen) {
                    this.$set(item, "__dueDialogOpen", false)
                }
                this.selectedTask = null
            },

            clearDueDate(item) {
                item.dueDate = null
                this.closeDueDateDialog(item)
            },
            waitAndFocusDatePicker(item, index) {
                // wartet, bis der v-menu-Content sichtbar ist, dann Fokus in die erste Datumzelle
                const menuClass = `due-menu-${item._id || index}`
                let tries = 0

                const attemptFocus = () => {
                    const container = document.querySelector(`.${menuClass}`)
                    const picker = this.$refs[`dp-${item._id || index}`]
                    const root = picker && picker.$el ? picker.$el : null

                    const visible = container && container.offsetParent !== null // sichtbar nach Transition
                    if (visible && root) {
                        // Bevorzugt aktive Datumstaste, sonst erste Taste im Grid
                        const candidate = root.querySelector(".v-date-picker-table .v-btn--active") || root.querySelector(".v-date-picker-table button") || root.querySelector('.v-btn,[tabindex="0"]')

                        if (candidate && typeof candidate.focus === "function") {
                            candidate.focus()
                            return
                        }
                    }
                    if (tries < 12) {
                        // ~2 Repaints + Transition-Puffer (12 * ~16ms ≈ 192ms)
                        tries++
                        requestAnimationFrame(attemptFocus)
                    }
                }

                // 1. Warten bis Vue gerendert hat, 2. dann über RAF bis nach der Transition
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(attemptFocus)
                    })
                })
            },
            focusCurrentStep() {
                if (this._suppressPanelFocus) {
                    this._suppressPanelFocus = false
                    return
                }
                const tab = document.getElementById(`step-tab-${this.currentStep}`)
                if (tab && tab.focus) tab.focus()
                this.$nextTick(() => {
                    const panel = document.getElementById(`step-panel-${this.currentStep}`)
                    if (!panel) return
                    const focusable = panel.querySelector('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')
                    if (focusable && focusable.focus) focusable.focus()
                })
            },
            toPickerDate(val) {
                // Gibt einen YYYY-MM-DD String oder null zurück
                if (!val) return null
                // wenn schon korrekt
                if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val

                // versuche in Date zu konvertieren
                const d = val instanceof Date ? val : new Date(val)
                if (isNaN(d.getTime())) return null

                const yyyy = d.getFullYear()
                const mm = String(d.getMonth() + 1).padStart(2, "0")
                const dd = String(d.getDate()).padStart(2, "0")
                return `${yyyy}-${mm}-${dd}`
            },
            setDueDate(item, val) {
                // val ist vom DatePicker bereits YYYY-MM-DD
                item.dueDate = val
                this.closeDueDateMenu()
            },
            formatDateShort(d) {
                if (!d) return ""
                // erlaubt YYYY-MM-DD, Date, ISO, Timestamp
                const date = typeof d === "string" ? new Date(d) : d instanceof Date ? d : new Date(Number(d))
                if (isNaN(date.getTime())) return ""
                return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date)
            },
            async createTaskMailNotificaton(taskdata, users, template, subject, additionalData = {}) {
                try {
                    console.log(this.project)

                    console.log(taskdata)
                    let mailData = []
                    if (users) {
                        let userMailData = users.map((userData) => {
                            let obj = { ...additionalData }
                            let name = userData.username.split(" ")[0] || userData.username
                            obj.address = userData.user
                            obj.firstname = name
                            obj.button = this.obi
                                ? "https://apollon.obi.com" + "/content-projects?ow=" + this.project._id + "&s=" + this.currentStep
                                : process.env.VUE_APP_URL + "/content-projects?ow=" + this.project._id + "&s=" + this.currentStep
                            obj.projectTitle = this.project.title
                            obj.taskTitle = taskdata.title
                            obj.template = template
                            obj.subject = subject
                            obj.dueDate = taskdata.dueDate ? taskdata.dueDate : "-"
                            return obj
                        })
                        mailData = [...mailData, ...userMailData]
                    }

                    let post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        mailData: mailData
                    }

                    const url = process.env.VUE_APP_APIURL + "/cs/notify-user"
                    let response_data = await this.$helpers.axiosPost(url, post_data)
                    console.log(response_data.data)
                } catch (error) {
                    console.log(error)
                }
            },
            async updateCurrentStage() {
                try {
                    let active_stage = this.getCurrentStage()
                    if (this.project.workflow && this.project.workflow.active_stage._id == active_stage._id) return // nothing to update

                    const workflow_data = {
                        workflow_id: this.workflow._id,
                        active_stage: { _id: active_stage._id, title: active_stage.title }
                    }
                    let post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        workflow_data: workflow_data
                    }
                    this.project.workflow = workflow_data
                    console.log(workflow_data.active_stage.title)
                    // console.log(post_data)
                    const url = process.env.VUE_APP_APIURL + "/cs/save/workflow-stage"

                    let response_data = await this.$helpers.axiosPost(url, post_data)
                    console.log(response_data.data)
                } catch (error) {
                    console.log(error)
                }
            },
            async saveWorkflow() {
                const url = process.env.VUE_APP_APIURL + "/cs/save/workflow"
                let post_data = {
                    company_id: localStorage.company_id,
                    workflow: this.workflow
                }
                let response_data = await this.$helpers.axiosPost(url, post_data)
                console.log(response_data.data)
            },
            async fetchWorkflow() {
                try {
                    console.log(this.project)
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id
                    }
                    const url = process.env.VUE_APP_APIURL + "/cs/get/workflow"
                    let response_data = await this.$helpers.axiosPost(url, post_data)
                    if (response_data.data.workflow) {
                        this.workflow = response_data.data.workflow
                        if (!this.stageToShow) {
                            let stageTitle = this.getCurrentStage().title
                            if (stageTitle == "Finished") {
                                this.currentStep = this.workflow.stages.length
                            } else {
                                let currentStep = this.workflow.stages.findIndex((s) => s.title == this.getCurrentStage().title)
                                currentStep++
                                this.currentStep = currentStep ? currentStep : 1
                            }
                        } else {
                            this.currentStep = this.stageToShow
                        }
                    } else {
                        console.log("workflow does not exists")
                        this.setDefaultWorkflow()
                    }
                } catch (error) {
                    console.log(error)
                }
            },
            async setDefaultWorkflow() {
                try {
                    let workflow = JSON.parse(JSON.stringify(this.default_workflow))
                    workflow.project_id = this.project._id

                    const post_data = {
                        company_id: localStorage.company_id,
                        workflow: workflow
                    }
                    const url = process.env.VUE_APP_APIURL + "/cs/set/workflow"
                    let response_data = await this.$helpers.axiosPost(url, post_data)

                    console.log(response_data.data)
                    this.workflow = response_data.data.workflow
                } catch (error) {
                    console.log(error)
                }
            },
            getCurrentStage() {
                // Iteriere durch die Stufen (stages) und finde die erste, in der nicht alle Aufgaben abgeschlossen sind
                for (let i = 0; i < this.workflow.stages.length; i++) {
                    const stage = this.workflow.stages[i]
                    if (!this.isStageComplete(stage)) {
                        return stage
                    }
                }
                // Falls alle Stufen abgeschlossen sind, gib letzte zurück
                return { title: "Finished" }
            },
            assignResponsible(respData) {
                console.log("RESPONSIBLE")
                console.log(respData)
                if (respData.user) {
                    this.selectedTask.responsible.user = respData.user
                }
                if (respData.groups) {
                    this.selectedTask.responsible.groups = respData.groups
                }
                // if (respData.content_suite_group_id == 2)
                this.$emit("addAuthors", this.selectedTask.responsible)

                this.createTaskMailNotificaton(this.selectedTask, this.selectedTask.responsible.user, "notifiy_new_taks_mail_en.html", "A task has been assigned to you!")
                console.log(this.workflow)
            },

            scrollToBottom() {
                console.log("scrollToBottom")
                const commentList = this.$refs.commentList.$el
                console.log(commentList)
                // Scrolle nach unten, um den neuen Kommentar anzuzeigen
                commentList.scrollTop = commentList.scrollHeight
            },
            addNewComment() {
                if (this.newComment.length == 0) return
                const newC = {
                    user: {
                        user: this.CryptoJS.AES.decrypt(localStorage.user, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8), // Beispiel-Benutzer-ID
                        username: this.CryptoJS.AES.decrypt(localStorage.username, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8) // Beispiel-Benutzername
                    },
                    content: this.newComment,
                    date: new Date()
                }
                this.selectedTask.comments.push(newC)

                this.$nextTick(() => {
                    this.scrollToBottom()
                })

                let additionalData = {
                    comment_text: newC.content,
                    comment_time: this.$helpers.formatRelativeDate(newC.date),
                    comment_name: newC.user.username,
                    comment_initials: this.$helpers.getInitials(newC.user.username)
                }
                console.log(this.userToNotify.user)
                if (this.userToNotify.user.length) {
                    this.createTaskMailNotificaton(this.selectedTask, this.userToNotify.user, "notifiy_new_comment_mail_en.html", "New Comment in Content Project!", additionalData)
                }
                this.newComment = ""
            },
            openDueDateMenu(item, index, evt) {
                // blur the activator so focus is free to move inside the menu
                if (evt && evt.currentTarget && typeof evt.currentTarget.blur === "function") {
                    evt.currentTarget.blur()
                }
                this.selectedTask = item
                this.dueDateFor = item
                this.dueDateRef = `dp-${item._id || index}`
                // actual focusing happens when menu reports "open" via @input
            },

            onDateMenuToggle(val) {
                if (val) {
                    // menu just opened: content is already rendered because of :eager
                    this.$nextTick(() => {
                        setTimeout(() => this.focusDatePicker(), 0)
                    })
                } else {
                    this.closeDueDateMenu()
                }
            },
            closeDueDateMenu() {
                this.selectedTask = null
                this.dueDateFor = null
                this.dueDateRef = null
            },
            focusDatePicker() {
                if (!this.dueDateRef) return
                const comp = this.$refs[this.dueDateRef]
                const root = comp && comp.$el ? comp.$el : null
                if (!root) return

                // Prefer the currently active day, else the first day button
                const candidate =
                    root.querySelector(".v-date-picker-table .v-btn--active") ||
                    root.querySelector(".v-date-picker-table .v-btn") ||
                    root.querySelector(".v-date-picker-table button") ||
                    root.querySelector('button, [tabindex]:not([tabindex="-1"])')

                if (candidate && typeof candidate.focus === "function") {
                    candidate.focus()
                } else {
                    // final fallback – try again on next frame
                    this.$nextTick(() => {
                        const alt = root.querySelector(".v-date-picker-table .v-btn") || root.querySelector('button, [tabindex]:not([tabindex="-1"])')
                        if (alt && typeof alt.focus === "function") alt.focus()
                    })
                }
            },
            openNewTaskDialog(stageIndex) {
                this.selectedStageIndex = stageIndex
                this.newTaskDialog = true
            },
            addNewTask() {
                if (this.newTaskTitle.trim() !== "") {
                    const id = "t_" + Math.random().toString(36).slice(2)
                    const newTask = { _id: id, title: this.newTaskTitle.trim(), responsible: { user: [], groups: [] }, dueDate: null, completionDate: null, completed: false, comments: [] }
                    this.workflow.stages[this.selectedStageIndex].tasks.push(newTask)
                    this.closeNewTaskDialog()
                }
            },

            closeNewTaskDialog() {
                this.newTaskDialog = false
                this.newTaskTitle = ""
                this.selectedStageIndex = null
            },
            checkTask(row) {
                if (!row.completed) {
                    row.completed = true
                    row.completionDate = new Date()
                    this.createTaskMailNotificaton(row, this.userToNotify.user, "notifiy_task_completed_mail_en.html", "A task has been completed!")
                } else {
                    row.completed = false
                    row.completionDate = null
                }

                if (this.isStageComplete(this.workflow.stages[this.currentStep - 1])) {
                    setTimeout(() => {
                        if (this.currentStep < this.workflow.stages.length) this.currentStep++
                    }, 350)
                }
            },
            isOverdue(date) {
                const currentDate = new Date()
                const dueDate = new Date(date)
                // Vergleiche die aktuellen Zeitstempel mit dem Fälligkeitsdatum
                if (dueDate < currentDate) {
                    return true
                }
                return false
            },
            getRowClass(row) {
                if (row.completed) return "finished"
                if (row.dueDate) {
                    const currentDate = new Date()
                    const dueDate = new Date(row.dueDate)

                    // Vergleiche die aktuellen Zeitstempel mit dem Fälligkeitsdatum
                    if (dueDate < currentDate) {
                        return "overdue"
                    }
                }
            },
            openResponsibleDialog(item, evt) {
                this.selectedTask = item
                this._lastActivatorId = `resp-${item._id || ""}`
                this.saveAndBlurActivator(evt) // hast du schon
                this.responsibleDialog = true
            },
            closeResponsibleDialog(item) {
                this.selectedTask = null
                this.responsibleDialog = false
            },
            openCommentsSidebar(item, evt) {
                this.selectedTask = item

                // Speichere das auslösende Element (Button)
                const el = evt?.currentTarget || document.activeElement || null
                this._lastCommentActivator = el

                this.commentsSidebar = true

                this.$nextTick(() => {
                    this.scrollToBottom()
                })
            },
            closeCommentsSidebar() {
                this.commentsSidebar = false
                this.selectedTask = null
            },
            openDialog() {
                this.dialog = true
            },
            closeDialog() {
                this.dialog = false
                this.currentStep = 1
            },
            isStageComplete(stage) {
                return stage.tasks.length > 0 && stage.tasks.every((t) => !!t.completionDate || t.completed === true)
            },
            // === Focus helpers (add inside methods) ===
            saveAndBlurActivator(evt) {
                const el = (evt && evt.currentTarget) || document.activeElement || null
                this._lastActivatorEl = el || null
                this._lastActivatorId = el && el.getAttribute ? el.getAttribute("data-activator-id") : null
                if (el && typeof el.blur === "function") el.blur()
            },

            restoreActivatorFocus() {
                // einmalige Unterdrückung des Panel-Autofokus
                this._suppressPanelFocus = true

                const activatorId = this._lastActivatorId
                this._lastActivatorEl = null

                this.$nextTick(() => {
                    // scope: aktuelles Step-Panel, damit nicht der „erste“ Chip im DOM getroffen wird
                    const scope = document.getElementById(`step-panel-${this.currentStep}`) || this.$el
                    const byRef = this.$refs[activatorId]
                    const el = (byRef && byRef.$el) || (byRef && byRef.focus && byRef) || (scope && scope.querySelector(`[data-activator-id="${activatorId}"]`))

                    // falls Row neu gerendert hat, noch einen Frame warten
                    const focusIt = (node) => {
                        if (node && node.focus) node.focus()
                    }
                    if (el) {
                        focusIt(el)
                    } else {
                        requestAnimationFrame(() => {
                            const fallback = scope && scope.querySelector(`[data-activator-id="${activatorId}"]`)
                            if (fallback) focusIt(fallback)
                            else this.focusCurrentStep() // letzte Rettung
                        })
                    }
                })
            },
            focusFirstPickerButton(pickerRoot) {
                if (!pickerRoot) return
                // Prefer selected day, else first day button in the grid
                const target =
                    pickerRoot.querySelector(".v-date-picker-table .v-btn--active") ||
                    pickerRoot.querySelector(".v-date-picker-table button") ||
                    pickerRoot.querySelector('button, [tabindex]:not([tabindex="-1"])')
                if (target && typeof target.focus === "function") target.focus()
            }
        }
    }
</script>

<style scoped>
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    .inactive {
        border-color: rgb(237, 239, 241);
    }
    .active {
        border-color: var(--v-success-base) !important;
    }
    .comment-list {
        height: calc(100% - 120px); /* Stelle sicher, dass die Liste die volle Höhe der Sidebar einnimmt */
        overflow-y: auto; /* Füge eine Scroll-Leiste hinzu, wenn der Chat-Inhalt die Höhe überschreitet */
    }

    /* Stil für Chat-Nachrichten */
    .v-list-item {
        margin-bottom: 8px;
    }
    .comment-area {
        margin-bottom: 10px;
        margin-top: 10px;
    }
    .comment-date {
        margin-left: 5px;
        font-style: italic;
        font-size: 12px;
    }
    .comment-user {
        margin-left: 5px;
        font-weight: bold;
        font-size: 12px;
        padding-bottom: 6px;
    }
    .comment-text {
        margin-top: 5px;
        background-color: #ecfef9;
        border-radius: 20px;
        color: #1f1f1f;
        display: inline-block;
        max-width: 100%;
        padding: 10px 12px;
        box-sizing: border-box;
        white-space: pre-wrap;
        position: relative;
        width: fit-content;
        font-size: 0.875rem;
        line-height: 1.25rem;
        word-break: break-word;
        word-wrap: break-word;
    }
    /* Hier könntest du Stilregeln für deine Komponente hinzufügen */
</style>
<style>
    #workflowTaskTable > div > table > thead > tr > th {
        text-transform: uppercase;
        white-space: nowrap;
        font-weight: 100;
    }
    #workflowTaskTable .finished {
        background-color: #4daf502b !important;
        transition: background-color 0.5s ease-in-out;
    }
    #workflowTaskTable .overdue {
        background-color: #af4d4d2b !important;
    }
    #workflow .qchip {
        background-color: rgb(245, 245, 245) !important;
    }
    .theme--dark #workflow .qchip {
        background-color: black !important;
    }
    .workflowHeader {
        box-shadow: none;
        background-color: #f6f6f6;
    }
    .theme--dark .workflowHeader {
        box-shadow: none;
        background-color: #000000;
    }
</style>
